import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { setColor } from "../../../../../assets/styles/styles";
import Typography from "../../../../../components/common/text/Typography";
import { updateArcadeGames } from "../api/roomController";
import { ReactComponent as DropdownIcon } from "../../../../../assets/images/icons/Chevron.svg";
import LoadingIcon from "../../../../../components/common/loading/LoadingIcon";
import { default as Button } from "../../../../../components/common/button/Button";
import Banner from "../../../../../components/layout/ui/Banner";
import ToggleSwitch from "../../../../../components/common/switchToggle/ToggleSwitch";

const AddUpdateArcadeGames = ({ roomId, action, source, closeModal, data }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [successMessageBanner, setSuccessMessageBanner] = useState("");
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [gamesList, setGamesList] = useState("");
    const [title, setTitle] = useState("");
    const [subtitle, setSubtitle] = useState("");
    const [disabledAddGame, setDisabledAddGame] = useState(false);
    const [isCustom, setIsCustom] = useState(false);
    const [isDefault, setIsDefault] = useState(false);

    useEffect(() => {
        if (data) {
            setGamesList(data?.gamesList ? JSON.stringify(data.gamesList, null, 2) : "");
            setTitle(data?.title || "");
            setSubtitle(data?.subtitle || "");
        }
        if (source === "default") setIsDefault(true);
        else if (source === "room") setIsCustom(true);

        setIsLoading(false);
    }, [data]);

    const UpdateGames = async () => {
        let parsedGamesList;

        try {
            parsedGamesList = gamesList ? JSON.parse(gamesList) : [];
        } catch (err) {
            setIsError(true);
            setErrorMessage("Games list contains invalid JSON. Please fix it before submitting.");
            return;
        }

        if (!title || !subtitle) {
            setIsError(true);
            setErrorMessage("Title and Subtitle are required.");
            return;
        }

        const finalSource = isDefault ? "default" : "room";

        if (finalSource === "default") {
            const confirmed = window.confirm(
                "You are about to update the default arcade games.\nThis will affect ALL rooms using the shared default configuration.\n\nAre you sure you want to continue?"
            );
            if (!confirmed) return;
        }

        const payload = {
            title,
            subtitle,
            gamesList: parsedGamesList,
            gameId: data?.gameId,
        };

        try {
            const resp = await updateArcadeGames(roomId, finalSource, payload);

            if (resp?.status === 200 || resp?.status === 201) {
                setSuccessMessageBanner("Game updated successfully");
                setIsError(false);
                setErrorMessage("");
            } else {
                setIsError(true);
                setDisabledAddGame(true);
                setErrorMessage(resp?.response?.data?.message || "Failed to update game.");
            }
        } catch (err) {
            setDisabledAddGame(true);
            setIsError(true);
            setErrorMessage(err?.response?.data?.message || "An unexpected error occurred.");
        }
    };

    const handleGamesListChange = (e) => {
        const value = e.target.value;
        setGamesList(value);
        try {
            JSON.parse(value);
            setIsError(false);
            setErrorMessage("");
            setDisabledAddGame(false);
        } catch {
            setIsError(true);
            setErrorMessage("Games list JSON is invalid.");
            setDisabledAddGame(true);
        }
    };

    useEffect(() => {
        if (successMessageBanner) {
            setDisabledAddGame(true);
            const timer = setTimeout(() => {
                closeModal();
                window.location.reload();
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessageBanner, closeModal]);

    const handleToggleChangeDefault = () => {
        setIsDefault((prev) => !prev);
        setIsCustom(false);
    };

    const handleToggleChangeCustom = () => {
        setIsCustom((prev) => !prev);
        setIsDefault(false);
    };

    const shouldShowForm =
        !isLoading && ((source === "default" && (!isCustom || isCustom)) || (source === "room" && !isDefault));

    return (
        <>
            <Banner
                width="100%"
                zIndex="1000"
                message={successMessageBanner}
                type="SUCCESS"
                openBanner={!!successMessageBanner}
                setOpenBanner={setSuccessMessageBanner}
                topDisplay
                showCloseButton
                gameScreen={false}
                top="0px"
                left="0"
            />

            <ModalContent>
                <Table>
                    <GeneralContainer>
                        <Typography variant="h2PurpleHeading" margin="0 0 20px 0">
                            {action === "Add" ? "Add Arcade Games to Room" : "Update Arcade Games"}
                        </Typography>

                        {isLoading && (
                            <LoadingDiv>
                                <LoadingIcon />
                            </LoadingDiv>
                        )}

                        <TableRow>
                            <TableColumn>
                                {source === "default" && (
                                    <StyledNote>
                                        This room is currently using the default arcade games set.{" "}
                                        <YellowText>
                                            Any changes made to the default games will apply to all rooms that use this
                                            shared default configuration.{" "}
                                        </YellowText>
                                        For updating the default games, continue with the form below. <br />
                                        <br />
                                        To customize games for this room only, enable the custom arcade games option.
                                    </StyledNote>
                                )}

                                {source === "room" && (
                                    <StyledNote>
                                        This room has a custom set of arcade games. You can modify the games for this
                                        room without affecting the default arcade games or switch it to use the default
                                        configuration.
                                    </StyledNote>
                                )}

                                <ToggleGroup>
                                    {source === "default" && (
                                        <ToggleRow>
                                            <Label>Customize arcade games for this room only</Label>
                                            <ToggleSwitch
                                                id="customToggle"
                                                value={isCustom}
                                                onChange={handleToggleChangeCustom}
                                            />
                                        </ToggleRow>
                                    )}
                                    {source === "room" && (
                                        <ToggleRow>
                                            <Label>Want to change the default games?</Label>
                                            <ToggleSwitch
                                                id="defaultToggle"
                                                value={isDefault}
                                                onChange={handleToggleChangeDefault}
                                            />
                                        </ToggleRow>
                                    )}
                                </ToggleGroup>
                            </TableColumn>
                        </TableRow>

                        {source === "default" && isCustom && (
                            <Typography variant="h2PurpleHeading" margin="0 0 20px 0">
                                Customize Arcade Games for Room
                            </Typography>
                        )}

                        {shouldShowForm && (
                            <>
                                <TableRow>
                                    <TableColumn>
                                        <Label>Room Id</Label>
                                        <InputBox type="text" value={roomId} disabled />
                                    </TableColumn>
                                </TableRow>

                                <TableRow>
                                    <TableColumn>
                                        <Label>Title</Label>
                                        <InputBox
                                            type="text"
                                            value={title}
                                            placeholder="Enter game title"
                                            onChange={(e) => setTitle(e.target.value)}
                                            errorMessage={!title ? "Title is required" : ""}
                                        />
                                    </TableColumn>
                                </TableRow>

                                <TableRow>
                                    <TableColumn>
                                        <Label>Subtitle</Label>
                                        <InputBox
                                            type="text"
                                            placeholder="Enter game subtitle"
                                            value={subtitle}
                                            onChange={(e) => setSubtitle(e.target.value)}
                                            errorMessage={!subtitle ? "Subtitle is required" : ""}
                                        />
                                    </TableColumn>
                                </TableRow>

                                <TableRow>
                                    <TableColumn>
                                        <Label>Games List</Label>
                                        <TEXTAREA
                                            value={gamesList}
                                            placeholder="Enter games list in JSON format"
                                            onChange={handleGamesListChange}
                                            style={{ width: "100%", height: "300px" }}
                                        />
                                    </TableColumn>
                                </TableRow>
                            </>
                        )}

                        {isError && <MessageDesc>{errorMessage}</MessageDesc>}
                        <TableRow>
                            <Button
                                variant="PinkButton"
                                buttonWidth="100%"
                                label={action === "Add" ? "Add Game" : "Update Game"}
                                disabled={disabledAddGame}
                                onClick={UpdateGames}
                            />
                        </TableRow>
                    </GeneralContainer>
                </Table>
            </ModalContent>
        </>
    );
};

export default AddUpdateArcadeGames;

// Styled Components (unchanged except spacing cleanup)
const LoadingDiv = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    color: ${setColor.inputText};
`;

const Table = styled.div`
    display: flex;
    flex-direction: column;
    padding: 1rem;
    border-radius: 5px;
    margin-top: 15px;
`;

const TableRow = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 10px;
`;

const TableColumn = styled.div`
    flex: 1;
    padding: 5px;
`;

const Label = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
`;

const InputBox = styled.input`
    padding: 10px;
    border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
    border-radius: 5px;
    color: ${setColor.inputText};
    width: 100%;
    background-color: transparent;
    margin-bottom: ${(props) => (props.errorMessage ? "0.5rem" : "0rem")};
    &:disabled {
        background-color: ${setColor.grey1};
    }
`;

const TEXTAREA = styled.textarea`
    border: 1px solid ${setColor.grey1};
    border-radius: 5px;
    color: ${setColor.inputText};
    width: 100%;
    background-color: transparent;
`;

const MessageDesc = styled.div`
    font-size: 14px;
    color: ${setColor.errorColor};
    margin: 0rem 0rem 0.5rem 0rem;
`;

const StyledNote = styled.div`
    background-color: #f0f4f8;
    padding: 1rem;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    color: #333;
    margin-bottom: 1rem;
`;

const ToggleGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const YellowText = styled.span`
    color: ${setColor.warning};
    font-weight: bold;
`;

const GeneralContainer = styled.div`
    width: 100%;
    font-family: "Barlow-Regular", Helvetica;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    @media (max-width: 768px) {
        font-size: 14px;
    }
`;
